<template>
  <v-container>
    <v-card flat color="transparent">
      <v-card-title class="white--text text-h5 font-weight-bold">Stellenanzeigen auf Webseite</v-card-title>
      <v-card-subtitle class="white--text pb-4"> Verwalte hier die Job-Angebote, die auf der Webseite angezeigt werden. </v-card-subtitle>
      <v-switch v-model="showVisible" :label="showVisible ? 'Nur sichtbare anzeigen' : 'Nur versteckte anzeigen'"></v-switch>
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-card @click="openAddDialog" class="item-card text-center d-flex flex-column justify-center" min-height="220" hover>
            <v-icon size="60" color="primary">mdi-plus-box</v-icon>
            <v-card-title class="justify-center pt-3">Neue Anzeige</v-card-title>
          </v-card>
        </v-col>

        <v-col v-for="job in jobOffers" v-if="(showVisible && job.visible) || (!showVisible && !job.visible)" :key="job._id" cols="12" sm="6" md="4">
          <v-card @click="openEditDialog(job)" class="item-card text-center d-flex flex-column" min-height="220" hover>
            <div class="status-indicator" :class="job.visible ? 'is-visible' : 'is-hidden'">
              <v-icon small color="white">{{ job.visible ? "mdi-eye" : "mdi-eye-off" }}</v-icon>
              <span class="ml-1">{{ job.visible ? "Sichtbar" : "Versteckt" }}</span>
            </div>

            <div class="item-card-icon-container">
              <v-icon size="50" color="primary">mdi-briefcase-outline</v-icon>
            </div>

            <v-card-title class="justify-center pt-3 pb-1">{{ job.title || job.type.title }}</v-card-title>
            <v-card-text class="white--text font-weight-light">
              {{ job.durationType || "Kein Anstellungstyp" }}
              <span v-if="job.durationTitle">| {{ job.durationTitle }}</span>
            </v-card-text>
            <v-card-subtitle class="caption"> Zuletzt bearbeitet: {{ formatDate(job.updatedAt) }} </v-card-subtitle>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="dialog" max-width="800px" persistent scrollable>
      <v-card dark>
        <v-card-title class="text-h5">{{ formTitle }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4" style="max-height: 70vh">
          <v-select
            v-if="!editedItem._id"
            v-model="selectedJobType"
            :items="availableJobTypes"
            item-text="title"
            label="Job-Vorlage auswählen"
            return-object
            @change="initializeNewJobOffer"
            outlined
            class="mb-4"></v-select>

          <v-form v-if="editedItem.type">
            <v-row>
              <v-col cols="12">
                <v-text-field v-model="editedItem.title" label="Titel der Stellenanzeige" outlined></v-text-field>
                <v-checkbox v-model="editedItem.visible" label="Sichtbar auf der Webseite"></v-checkbox>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.durationType"
                  :items="['Festanstellung', 'Teilzeit', 'Lehrstelle', 'Praktikum', 'Ausbildung', 'Stundenlohn']"
                  label="Anstellungstyp"
                  outlined></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.durationTitle"
                  label="Prozente (z.B. 50% - 60%)"
                  :rules="[percentValidation]"
                  outlined></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-divider class="my-3"></v-divider>
                <h3 class="text-subtitle-1 font-weight-bold mb-2">Bewerbungsempfänger (HR / Studioleitung)</h3>
                <p class="caption grey--text mb-4">
                  Diese Personen erhalten neue Bewerbungen per E-Mail und Push-Nachricht. Es werden nur Mitarbeiter angezeigt, die berechtigt sind,
                  Bewerbungen zu bearbeiten.
                </p>

                <v-card outlined color="rgba(255,255,255,0.05)" class="pa-3 mb-4">
                  <v-autocomplete
                    v-model="userToAdd"
                    :items="filteredAvailableUsers"
                    :loading="isLoadingUsers"
                    :item-text="userDisplayName"
                    item-value="_id"
                    return-object
                    label="Mitarbeiter suchen und hinzufügen..."
                    outlined
                    dense
                    hide-details
                    clearable
                    no-data-text="Keine berechtigten Mitarbeiter gefunden"
                    @change="addAssignedUser">
                    <template v-slot:item="{ item }">
                      <v-list-item-avatar color="grey darken-3" size="32">
                        <span class="ma-auto white--text caption"> {{ (item.vorname || "U").charAt(0) }}{{ (item.nachname || "").charAt(0) }} </span>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.vorname }} {{ item.nachname }}</v-list-item-title>
                        <v-list-item-subtitle class="caption">{{ item.email }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-card>

                <v-list dense color="transparent" class="mb-4">
                  <v-list-item v-for="(user, index) in editedItem.assignedUsers" :key="user._id || index" class="px-0">
                    <v-list-item-avatar color="grey darken-3" size="32">
                      <span class="ma-auto white--text caption"> {{ (user.vorname || "U").charAt(0) }}{{ (user.nachname || "").charAt(0) }} </span>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ user.vorname }} {{ user.nachname }}</v-list-item-title>
                      <v-list-item-subtitle class="caption">{{ user.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon small color="red lighten-2" @click="removeAssignedUser(index)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-alert v-if="editedItem.assignedUsers.length === 0" type="warning" text dense>
                    Es wurde noch keine zuständige Person zugewiesen!
                  </v-alert>
                </v-list>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveJobOffer" :disabled="!isJobOfferValid" :loading="isSaving">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@/api"

export default {
  data() {
    return {
      jobOffers: [],
      availableJobTypes: [],
      selectedJobType: null,
      dialog: false,
      isSaving: false,
      availableUsers: [],
      isLoadingUsers: false,
      userToAdd: null,
      editedItem: {},
      showVisible: true,
      defaultItem: {
        _id: null,
        type: null,
        title: "",
        visible: false,
        durationType: "",
        durationTitle: "",
        assignedUsers: [],
        updatedAt: new Date().toISOString(),
      },
      percentValidation: (value) => {
        if (!value) return true
        const regex = /^(\d{1,3}%|\d{1,3}%\s*-\s*\d{1,3}%)$/
        return regex.test(value) || 'Format: "50%" oder "50% - 60%"'
      },
    }
  },
  computed: {
    filteredAvailableUsers() {
      const assignedIds = (this.editedItem.assignedUsers || []).map((u) => u._id)
      return this.availableUsers.filter((u) => !assignedIds.includes(u._id))
    },
    formTitle() {
      return this.editedItem._id ? "Stellenanzeige bearbeiten" : "Neue Stellenanzeige erstellen"
    },
    isJobOfferValid() {
      if (!this.editedItem.type) return false
      const regex = /^(\d{1,3}%|\d{1,3}%\s*-\s*\d{1,3}%)$/
      return regex.test(this.editedItem.durationTitle) || this.editedItem.durationTitle === ""
    },
  },
  async created() {
    // Zuerst die berechtigten User laden, damit das Mapping in openEditDialog
    // garantiert klappt - dann erst die JobOffers
    await this.loadEligibleUsers()
    await this.init()
  },
  methods: {
    async loadEligibleUsers() {
      this.isLoadingUsers = true
      try {
        this.availableUsers = await api.jobApplications.fetchEligibleUsers()
      } catch (error) {
        if (this.$toast) this.$toast.error("Berechtigte Mitarbeiter konnten nicht geladen werden")
      } finally {
        this.isLoadingUsers = false
      }
    },
    userDisplayName(user) {
      if (!user) return ""
      return `${user.vorname || ""} ${user.nachname || ""} (${user.email || ""})`.trim()
    },
    addAssignedUser(user) {
      if (!user) return
      const alreadyAdded = this.editedItem.assignedUsers.some((u) => u._id === user._id)
      if (!alreadyAdded) {
        this.editedItem.assignedUsers.push(user)
      }
      this.$nextTick(() => {
        this.userToAdd = null
      })
    },
    async init() {
      try {
        this.jobOffers = await api.fetchJobOffers()
        this.availableJobTypes = await api.fetchJobTypes()
      } catch (error) {
        if (this.$toast) this.$toast.error("Fehler beim Laden der Daten")
      }
    },
    openAddDialog() {
      this.selectedJobType = null
      this.editedItem = { ...this.defaultItem, assignedUsers: [] }
      this.userToAdd = null
      this.dialog = true
    },
    openEditDialog(item) {
      this.editedItem = JSON.parse(JSON.stringify(item))

      if (!this.editedItem.assignedUsers) {
        this.$set(this.editedItem, "assignedUsers", [])
      } else if (this.editedItem.assignedUsers.length > 0 && typeof this.editedItem.assignedUsers[0] === "string") {
        // IDs gegen die schon geladenen availableUsers matchen
        const populated = this.editedItem.assignedUsers.map((id) => this.availableUsers.find((u) => u._id === id)).filter(Boolean)
        this.$set(this.editedItem, "assignedUsers", populated)
      }

      this.userToAdd = null
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
    },
    initializeNewJobOffer() {
      if (this.selectedJobType) {
        this.editedItem = {
          ...this.defaultItem,
          type: this.selectedJobType,
          title: this.selectedJobType.title,
          assignedUsers: [],
        }
      }
    },
    removeAssignedUser(index) {
      this.editedItem.assignedUsers.splice(index, 1)
    },
    async saveJobOffer() {
      if (!this.isJobOfferValid) return
      this.isSaving = true

      // Extrahiere nur die IDs für das Backend
      const userIds = this.editedItem.assignedUsers.map((user) => user._id || user)

      const payload = {
        type: this.editedItem.type._id,
        title: this.editedItem.title,
        visible: this.editedItem.visible,
        durationType: this.editedItem.durationType,
        durationTitle: this.editedItem.durationTitle,
        assignedUsers: userIds,
      }

      try {
        if (this.editedItem._id) {
          await api.updateJobOffer(this.editedItem._id, payload)
        } else {
          await api.createJobOffer(payload)
        }
        if (this.$toast) this.$toast.success("Job-Anzeige erfolgreich gespeichert")
        this.closeDialog()
        this.init()
      } catch (error) {
        if (this.$toast) this.$toast.error("Fehler beim Speichern der Job-Anzeige")
      } finally {
        this.isSaving = false
      }
    },
    formatDate(date) {
      return this.$moment(date).format("DD.MM.YY HH:mm") + " Uhr"
    },
  },
}
</script>
